/**
 * Webhooks de saída (0153) do lado da tela: o catálogo de eventos, os tipos e a leitura do que o
 * banco devolve. O envio, a assinatura e a conferência do destino ficam na borda `webhooks`.
 */

export const EVENTOS_DE_WEBHOOK = [
    { id: 'agendamento.criado', grupo: 'Agendamentos', rotulo: 'Agendamento criado', descricao: 'Novo horário marcado. Bloqueios de agenda não entram.' },
    { id: 'agendamento.alterado', grupo: 'Agendamentos', rotulo: 'Agendamento alterado', descricao: 'Mudou horário, profissional, procedimento, sala ou situação — com o que era antes.' },
    { id: 'agendamento.cancelado', grupo: 'Agendamentos', rotulo: 'Agendamento cancelado', descricao: 'O horário foi cancelado.' },
    { id: 'contato.criado', grupo: 'Contatos e leads', rotulo: 'Contato criado', descricao: 'Novo paciente ou contato cadastrado.' },
    { id: 'lead.criado', grupo: 'Contatos e leads', rotulo: 'Lead criado', descricao: 'Nova oportunidade no funil.' },
    { id: 'lead.etapa_alterada', grupo: 'Contatos e leads', rotulo: 'Lead mudou de etapa', descricao: 'A oportunidade avançou ou voltou no funil — com a etapa anterior.' },
    { id: 'pagamento.recebido', grupo: 'Financeiro', rotulo: 'Pagamento recebido', descricao: 'Pagamento lançado numa conta a receber.' },
    { id: 'mensagem.recebida', grupo: 'Comunicação', rotulo: 'Mensagem recebida', descricao: 'Mensagem que chegou num canal conectado, com o texto.' },
] as const;

export type EventoDeWebhook = typeof EVENTOS_DE_WEBHOOK[number]['id'];

const IDS_DOS_EVENTOS = new Set<string>(EVENTOS_DE_WEBHOOK.map((e) => e.id));

export function rotuloDoEvento(evento: string): string {
    if (evento === 'webhook.teste') return 'Teste';
    return EVENTOS_DE_WEBHOOK.find((e) => e.id === evento)?.rotulo ?? evento;
}

export interface Webhook {
    id: string;
    nome: string;
    url: string;
    eventos: EventoDeWebhook[];
    /** Os quatro últimos caracteres do segredo. O segredo inteiro não volta. */
    segredoDica: string;
    ativo: boolean;
    falhasSeguidas: number;
    ultimaEntregaEm: string | null;
    ultimoErro: string | null;
    autorNome: string | null;
    criadoEm: string;
    pendentes: number;
    entregues24h: number;
    desistencias24h: number;
}

export interface DadosDoWebhook {
    nome: string;
    url: string;
    eventos: EventoDeWebhook[];
    ativo: boolean;
}

export interface WebhookCriado {
    webhook: Webhook;
    /** Aparece uma vez, nesta resposta. */
    segredo: string;
}

export interface EntregaDeWebhook {
    id: string;
    evento: string;
    criadoEm: string;
    tentativas: number;
    proximaTentativaEm: string;
    entregueEm: string | null;
    desistiuEm: string | null;
    statusHttp: number | null;
    erro: string | null;
    carga: unknown;
}

export type SituacaoDaEntrega = 'entregue' | 'na_fila' | 'tentando' | 'desistiu';

export function situacaoDaEntrega(e: EntregaDeWebhook): SituacaoDaEntrega {
    if (e.entregueEm) return 'entregue';
    if (e.desistiuEm) return 'desistiu';
    return e.tentativas > 0 ? 'tentando' : 'na_fila';
}

/** Com cinco falhas seguidas o banco avisa no sininho; a lista mostra o mesmo corte. */
export const FALHAS_PARA_ALERTA = 5;

const texto = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

export function paraWebhook(w: Record<string, unknown>): Webhook {
    return {
        id: String(w.id),
        nome: String(w.nome ?? ''),
        url: String(w.url ?? ''),
        eventos: ((w.eventos ?? []) as unknown[]).map(String).filter((e): e is EventoDeWebhook => IDS_DOS_EVENTOS.has(e)),
        segredoDica: String(w.segredo_dica ?? ''),
        ativo: Boolean(w.ativo),
        falhasSeguidas: Number(w.falhas_seguidas ?? 0),
        ultimaEntregaEm: texto(w.ultima_entrega_em),
        ultimoErro: texto(w.ultimo_erro),
        autorNome: texto(w.autor_nome),
        criadoEm: String(w.criado_em ?? ''),
        pendentes: Number(w.pendentes ?? 0),
        entregues24h: Number(w.entregues_24h ?? 0),
        desistencias24h: Number(w.desistencias_24h ?? 0),
    };
}

export function paraEntregaDeWebhook(e: Record<string, unknown>): EntregaDeWebhook {
    return {
        id: String(e.id),
        evento: String(e.evento ?? ''),
        criadoEm: String(e.criado_em ?? ''),
        tentativas: Number(e.tentativas ?? 0),
        proximaTentativaEm: String(e.proxima_tentativa_em ?? ''),
        entregueEm: texto(e.entregue_em),
        desistiuEm: texto(e.desistiu_em),
        statusHttp: e.status_http === null || e.status_http === undefined ? null : Number(e.status_http),
        erro: texto(e.erro),
        carga: e.carga ?? null,
    };
}

/** O trecho que a tela mostra para conferir a assinatura no outro sistema (Node.js ou n8n, nó Code). */
export const EXEMPLO_DE_VERIFICACAO = `const crypto = require('crypto');

// segredo: o whsec_… mostrado ao criar o webhook
function assinaturaConfere(segredo, cabecalhos, corpoCru) {
  const id = cabecalhos['webhook-id'];
  const timestamp = cabecalhos['webhook-timestamp'];
  if (Math.abs(Date.now() / 1000 - Number(timestamp)) > 300) return false; // mais de 5 min: recuse
  const chave = Buffer.from(segredo.replace('whsec_', ''), 'base64');
  const esperada = crypto.createHmac('sha256', chave).update(\`\${id}.\${timestamp}.\${corpoCru}\`).digest('base64');
  return cabecalhos['webhook-signature'].split(' ').some((s) => s === \`v1,\${esperada}\`);
}`;
